import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, setDoc, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import './OnboardingChatbot.css';

// Bot avatar component
const BotAvatar = () => (
  <div className="bot-avatar">
    <span className="bot-emoji">😊</span>
  </div>
);



const INTERESTS = [
  { emoji: "🎶", label: "Music" },
  { emoji: "🍳", label: "Cooking" },
  { emoji: "🎨", label: "Art & Design" },
  { emoji: "🎥", label: "Movies & Series" },
  { emoji: "📚", label: "Books & Reading" },
  { emoji: "🌍", label: "Traveling" },
  { emoji: "🍷", label: "Wine & Drinks" },
  { emoji: "🧘", label: "Yoga & Meditation" },
  { emoji: "⚽", label: "Sports" },
  { emoji: "🎭", label: "Theater & Culture" },
  { emoji: "🧩", label: "Board Games" },
  { emoji: "🐾", label: "Animals & Pets" },
  { emoji: "🍰", label: "Baking" },
  { emoji: "💬", label: "Deep Conversations" },
  { emoji: "😂", label: "Humor & Memes" },
  { emoji: "📷", label: "Photography" },
  { emoji: "🕯️", label: "Shabbat Traditions" },
  { emoji: "👩‍🍳", label: "Hosting" },
  { emoji: "🌿", label: "Nature & Hiking" },
  { emoji: "🧠", label: "Psychology & People" },
  { emoji: "💻", label: "Tech & Startups" },
  { emoji: "☕", label: "Coffee Lovers" },
  { emoji: "🎤", label: "Singing & Karaoke" },
  { emoji: "💡", label: "Ideas & Innovation" }
];

// Message bubble component
const MessageBubble = ({ message, isUser, singleLine }) => (
  <div className={`message-container ${isUser ? 'user-message' : 'bot-message'}`}>
    {!isUser && <BotAvatar />}
    <div className="message-content">
      <div className={`message-bubble ${isUser ? 'user-bubble' : 'bot-bubble'}${singleLine ? ' single-line-bubble' : ''}`}>{message}</div>
    </div>
  </div>
);

const optionsQ1 = [
  { label: 'Individual', emoji: '🧑' },
  { label: 'Couple', emoji: '💑' },
  { label: 'Family', emoji: '👨‍👩‍👧‍👦' },
  { label: 'Group of Friends', emoji: '🧑‍🤝‍🧑' },
  { label: 'Other', emoji: '❓' },
];
const optionsQ2 = [
  { label: 'Stargazing & tea', emoji: '✨🍵' },
  { label: 'Board games & banter', emoji: '🎲🗣️' },
  { label: 'House full of guests', emoji: '🏠🎉' },
  { label: 'Netflix & leftovers', emoji: '🍕📺' },
];
const optionsQ5 = [
  { label: 'Einstein', emoji: '🧠' },
  { label: 'Beyoncé', emoji: '🎤' },
  { label: 'Adam Sandler', emoji: '😂' },
  { label: 'Grandma', emoji: '👵' },
  { label: 'Other', emoji: '❓' },
];
const optionsQ7 = [
  { label: 'Excited', emoji: '🤩' },
  { label: 'Curious', emoji: '🧐' },
  { label: 'Shy', emoji: '😳' },
  { label: 'Depends on the vibe', emoji: '🌈' },
];

const questions = [
  {
    text: "Who are you signing in as?",
    type: "choice",
    options: optionsQ1,
    key: "signingInAs"
  },
  {
    text: "What's a fun or surprising fact about you?",
    type: "open",
    key: "funFact"
  },
  {
    text: "What are your interests? (Select as many as you like!)",
    type: "multi-choice",
    options: INTERESTS,
    key: "interests"
  },
  {
    text: "What's your ideal Friday night?",
    type: "choice",
    options: optionsQ2,
    key: "idealFridayNight"
  },
  {
    text: "What's one thing you're always happy to talk about?",
    type: "open",
    key: "happyToTalkAbout"
  },
  {
    text: "What's your favorite part of Shabbat dinner?",
    type: "open",
    key: "favoriteShabbatPart"
  },
  {
    text: "Dream Shabbat guest?",
    type: "choice",
    options: optionsQ5,
    key: "dreamGuest"
  },
  {
    text: "What meal would you cook for your dream guest?",
    type: "open",
    key: "askGuest"
  },
  {
    text: "How do you feel about meeting new people at dinner?",
    type: "choice",
    options: optionsQ7,
    key: "meetingNewPeople"
  },
  {
    text: "What's something people are usually surprised to learn about you?",
    type: "open",
    key: "surprisedAboutYou"
  }
];

const branchingQuestions = {
  Individual: [
    {
      text: "What's one thing you love bringing to a dinner (besides food)?",
      type: "open",
      key: "bringToDinner"
    },
    {
      text: "If you had a podcast, what would it be about?",
      type: "open",
      key: "podcastAbout"
    }
  ],
  Couple: [
    {
      text: "How are you different from each other at the table?",
      type: "open",
      key: "differentAtTable"
    },
    {
      text: "What's a shared memory you love telling guests?",
      type: "open",
      key: "sharedMemory"
    }
  ],
  Family: [
    {
      text: "Is there a Shabbat tradition your family does that's unique?",
      type: "open",
      key: "uniqueTradition"
    },
    {
      text: "What is your family's favorite dish?",
      type: "open",
      key: "justRightDinner"
    }
  ],
  'Group of Friends': [
    {
      text: "How would you describe your group's vibe in 3 words?",
      type: "open",
      key: "groupVibe"
    },
    {
      text: "What's a story your group always tells at dinner?",
      type: "open",
      key: "groupStory"
    }
  ],
  Other: [
    {
      text: "What's one thing you love bringing to a dinner (besides food)?",
      type: "open",
      key: "bringToDinner"
    },
    {
      text: "If you had a podcast, what would it be about?",
      type: "open",
      key: "podcastAbout"
    }
  ]
};

const OnboardingChatbot = ({ onClose, startedFromProfile = false }) => {
  const chatContainerRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [editInput, setEditInput] = useState('');
  const [messages, setMessages] = useState([
    { text:"Let's make your Shabbat special! Just answer a few questions so we can get to know your vibe."

      , isUser: false }
  ]);
  const [input, setInput] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [visible, setVisible] = useState(true);
  const [showQuestion, setShowQuestion] = useState(false);
  const [questionSent, setQuestionSent] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [allQuestions, setAllQuestions] = useState([...questions]);
  const [isLoading, setIsLoading] = useState(true);
  const [multiSelect, setMultiSelect] = useState([]);
  // Only auto-scroll when a new message is added, not when editing
  const prevMessagesLength = useRef(messages.length);

  const renderInitialMultiChoice = () => (
      <div>
        <div className="interests-grid">
          {INTERESTS.map(option => (
              <button
                  key={option.label}
                  className={`interest-btn${multiSelect.includes(option.label) ? ' selected' : ''}`}
                  onClick={() => handleMultiSelect(option.label)}
                  type="button"
              >
                <span className="emoji">{option.emoji}</span> {option.label}
              </button>
          ))}
        </div>
        <div className="edit-controls" style={{ marginTop: 10 }}>
          <button
              className="send-button"
              onClick={handleMultiSend}
              disabled={multiSelect.length === 0}
          >
            <FiCheck />
          </button>
          <button className="skip-button" onClick={handleSkip}><FiX /></button>
        </div>
      </div>
  );
  useEffect(() => {
    if (messages.length > prevMessagesLength.current) {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
    prevMessagesLength.current = messages.length;
  }, [messages]);

  useEffect(() => {
    const loadAnswers = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }
      const answerRef = doc(db, 'shabbotAnswers', currentUser.uid);
      const answerSnap = await getDoc(answerRef);

      if (answerSnap.exists()) {
        const existingAnswers = answerSnap.data().answers;
        setAnswers(existingAnswers);

        const firstAnswer = existingAnswers[questions[0].key];
        const unique = branchingQuestions[firstAnswer] || branchingQuestions['Other'];
        const fullQuestionSet = [...questions, ...unique];
        setAllQuestions(fullQuestionSet);

        const newMessages = [];
        newMessages.push({ text: "Here are your current answers. Feel free to edit them!", isUser: false });

        let lastAnsweredIndex = -1;
        fullQuestionSet.forEach((q, index) => {
          if (Object.prototype.hasOwnProperty.call(existingAnswers, q.key)) {
            const answer = existingAnswers[q.key];
            newMessages.push({ text: q.text, isUser: false });
            newMessages.push({
              text: answer || '(skipped)',
              isUser: true,
              singleLine: true,
              key: q.key,
              type: q.type,
              options: q.options
            });
            lastAnsweredIndex = index;
          }
        });
        
        setMessages(newMessages);

        const nextStep = lastAnsweredIndex + 1;
        setStep(nextStep);

        if (nextStep < fullQuestionSet.length) {
          setTimeout(() => {
            setMessages(prev => [...prev, { text: fullQuestionSet[nextStep].text, isUser: false }]);
            setShowOptions(true);
          }, 400);
        } else {
          setShowConfirmation(true);
        }
      }
      setIsLoading(false);
    };
    loadAnswers();
  }, [currentUser]);

  // After greeting, send the first question as a bot message, then show options
  useEffect(() => {
    if (!isLoading && step === 0 && !showQuestion) {
      const timer = setTimeout(() => setShowQuestion(true), 700);
      return () => clearTimeout(timer);
    }
    if (!isLoading && showQuestion && !questionSent && step === 0) {
      setMessages(prev => ([...prev, { text: questions[0].text, isUser: false }]));
      setQuestionSent(true);
      setTimeout(() => setShowOptions(true), 350);
    }
  }, [step, showQuestion, questionSent, isLoading]);

  const currentQ = allQuestions[step];

  // Only show options/input if not on the greeting or the first question message
  const isGreeting = step === 0 && (!showQuestion || !questionSent);

  // Add this function to check if we should show the skip button
  const shouldShowSkipButton = () => {
    return step > 0; // Only show skip button after the first question
  };

  const handleOption = (option) => {
    // Special handling for the first question to set the full question set
    if (step === 0) {
      setAnswers(prev => ({ ...prev, [questions[0].key]: option }));
      setMessages(prev => ([
        ...prev,
        {
          text: option,
          isUser: true,
          singleLine: true,
          key: questions[0].key,
          type: questions[0].type,
          options: questions[0].options
        }
      ]));
      // Set the full question set: 8 standard + 2 unique
      const unique = branchingQuestions[option] || branchingQuestions['Other'];
      setAllQuestions([...questions, ...unique]);
      setShowOptions(false);
      setTimeout(() => {
        setMessages(prev => ([...prev, { text: questions[1].text, isUser: false }]));
        setStep(1);
        setShowOptions(true);
      }, 400);
      return;
    }
    setAnswers(prev => ({ ...prev, [currentQ.key]: option }));
    setMessages(prev => [
      ...prev,
      {
        text: option,
        isUser: true,
        singleLine: true,
        key: currentQ.key,
        type: currentQ.type,
        options: currentQ.options
      }
    ]);
    setShowOptions(false);
    setTimeout(() => {
      if (step + 1 < allQuestions.length) {
        setMessages(prev => ([...prev, { text: allQuestions[step + 1].text, isUser: false }]));
        setStep(step + 1);
        setShowOptions(true);
      } else {
        setShowConfirmation(true);
        setMessages(prev => ([...prev, { text: "Thanks! You're all set 🎉", isUser: false }]));
      }
    }, 400);
  };

  const handleInput = (e) => setInput(e.target.value);

  const handleSend = () => {
    if (!input.trim()) return;
    setAnswers(prev => ({ ...prev, [currentQ.key]: input.trim() }));
    setMessages(prev => [
      ...prev,
      {
        text: input.trim(),
        isUser: true,
        singleLine: true,
        key: currentQ.key,
        type: currentQ.type,
        options: currentQ.options
      }
    ]);
    setInput("");
    setShowOptions(false);
    setTimeout(() => {
      if (step + 1 < allQuestions.length) {
        setMessages(prev => ([...prev, { text: allQuestions[step + 1].text, isUser: false }]));
        setStep(step + 1);
        setShowOptions(true);
      } else {
        setShowConfirmation(true);
        setMessages(prev => ([...prev, { text: "Thanks! You're all set 🎉", isUser: false }]));
      }
    }, 400);
  };
  const handleSkip = () => {
    setAnswers(prev => ({ ...prev, [currentQ.key]: "" }));
    setMessages(prev => [
      ...prev,
      {
        text: "(skipped)",
        isUser: true,
        singleLine: true,
        key: currentQ.key,
        type: currentQ.type,
        options: currentQ.options
      }
    ]);
    setInput("");
    setShowOptions(false);
    setTimeout(() => {
      if (step + 1 < allQuestions.length) {
        setMessages(prev => ([...prev, { text: allQuestions[step + 1].text, isUser: false }]));
        setStep(step + 1);
        setShowOptions(true);
      } else {
        setShowConfirmation(true);
        setMessages(prev => ([...prev, { text: "Thanks! You're all set 🎉", isUser: false }]));
      }
    }, 400);
  };

  const handleClose = () => {
    if (onClose) onClose();
    else setVisible(false);
  };

  // Save or update answers in Firestore (by UID)
  const saveAnswersToFirestore = async (updatedAnswers = answers) => {
    if (!currentUser) return;
    try {
      await setDoc(doc(db, 'shabbotAnswers', currentUser.uid), {
        answers: updatedAnswers,
        uid: currentUser.uid,
        updatedAt: serverTimestamp(),
      });
      // אם יש interests, עדכן גם במסמך המשתמש
      if (updatedAnswers.interests) {
        await setDoc(doc(db, 'users', currentUser.uid), {
          interests: updatedAnswers.interests
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error saving answers:', error);
    }
  };

  // Call saveAnswersToFirestore when showConfirmation is set to true
  useEffect(() => {
    if (showConfirmation) {
      saveAnswersToFirestore();
    }
    // eslint-disable-next-line
  }, [showConfirmation]);

  // Edit handlers
  const handleEdit = (key, currentValue) => {
    setEditingKey(key);
    if (key === "interests") {
      setMultiSelect(
          typeof currentValue === "string"
              ? currentValue.split(',').map(s => s.trim()).filter(Boolean)
              : []
      );
    } else {
      setEditInput(Array.isArray(currentValue) ? currentValue.join('') : (currentValue || ''));
    }
  };
  const handleEditSave = (key, type, options) => {
    let newValue;
    if (key === "interests") {
      newValue = multiSelect.join(', ');
    } else {
      newValue = Array.isArray(editInput) ? editInput.join('') : editInput;
    }
    setAnswers(prev => {
      const updated = { ...prev, [key]: newValue };
      saveAnswersToFirestore(updated);
      return updated;
    });

    setMessages(prevMessages =>
        prevMessages.map(msg => (msg.key === key ? { ...msg, text: newValue } : msg))
    );

    setEditingKey(null);
    setEditInput('');
    setMultiSelect([]);
  };
  const handleEditCancel = () => {
    setEditingKey(null);
    setEditInput('');
  };

  // Render user answers with edit option
  const renderUserAnswer = (msg, idx, key, type, options) => {
    if (editingKey === key) {
      if (type === 'multi-choice') {
        return (
            <div>
              <div className="interests-grid">
                {INTERESTS.map(option => (
                    <button
                        key={option.label}
                        className={`interest-btn${multiSelect.includes(option.label) ? ' selected' : ''}`}
                        onClick={() => handleMultiSelect(option.label)}
                        type="button"
                    >
                      <span className="emoji">{option.emoji}</span> {option.label}
                    </button>
                ))}
              </div>
              <div className="edit-controls" style={{ marginTop: 10 }}>
                <button
                    className="send-button"
                    onClick={() => handleEditSave("interests", "multi-choice", INTERESTS)}
                    disabled={multiSelect.length === 0}
                >
                  <FiCheck />
                </button>
                <button className="skip-button" onClick={handleEditCancel}><FiX /></button>
              </div>
            </div>
        );
      }
      else if (type === 'choice' && Array.isArray(options)) {
        return (
            <div className="editing-choice-container">
                <div className="option-buttons-inside">
                    {options.map(option => (
                        <button
                            key={option.label}
                            className={`option-button${editInput === option.label ? ' selected' : ''}`}
                            onClick={() => setEditInput(option.label)}
                            style={{ fontWeight: editInput === option.label ? 700 : 400 }}
                        >
                            <span role="img" aria-label="emoji">{option.emoji}</span> {String(option.label)}
                        </button>
                    ))}
                </div>
                <div className="edit-controls">
                    <button className="skip-button" onClick={handleEditCancel}><FiX /></button>
                    <button className="send-button" onClick={() => handleEditSave(key, type, options)}><FiCheck /></button>
                </div>
            </div>
        );
      } else if (type === 'open') {
        return (
          <form onSubmit={e => { e.preventDefault(); handleEditSave(key, type); }} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              className="chatbot-input"
              value={editInput}
              onChange={e => setEditInput(e.target.value)}
              autoFocus
            />
            <button type="submit" className="send-button"><FiCheck /></button>
            <button type="button" className="skip-button" onClick={handleEditCancel}><FiX /></button>
          </form>
        );
      }
    }
    // Normal display with edit icon
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {msg.text}
        <FiEdit2 style={{ cursor: 'pointer', color: '#ff914d' }} onClick={() => handleEdit(key, msg.text)} />
      </span>
    );
  };

  if (!visible) return null;

// מוסיפים אחרי כל ה-useState
  const handleMultiSelect = (option) => {
    setMultiSelect(prev =>
        prev.includes(option)
            ? prev.filter(o => o !== option)
            : [...prev, option]
    );
  };

  const handleMultiSend = () => {
    setAnswers(prev => ({ ...prev, [currentQ.key]: multiSelect.join(', ') }));
    setMessages(prev => [
      ...prev,
      {
        text: multiSelect.join(', '),
        isUser: true,
        singleLine: true,
        key: currentQ.key,
        type: currentQ.type,
        options: currentQ.options
      }
    ]);
    setMultiSelect([]);
    setShowOptions(false);
    setTimeout(() => {
      if (step + 1 < allQuestions.length) {
        setMessages(prev => ([...prev, { text: allQuestions[step + 1].text, isUser: false }]));
        setStep(step + 1);
        setShowOptions(true);
      } else {
        setShowConfirmation(true);
        setMessages(prev => ([...prev, { text: "Thanks! You're all set 🎉", isUser: false }]));
      }
    }, 400);
  };

  return (
      <div className="chatbot-modal">
        <div className="chatbot-container">
          <div className="chatbot-header">
            <h2>Let's Get to Know You</h2>
            <button className="close-chatbot-x" onClick={handleClose} aria-label="Close">×</button>
          </div>
          <div className="chatbot-messages" ref={chatContainerRef}>
            {messages.map((msg, idx) => (
                <MessageBubble key={idx} message={msg.isUser ? renderUserAnswer(msg, idx, msg.key, msg.type, msg.options) : msg.text} isUser={msg.isUser} singleLine={msg.singleLine} />
            ))}
            {/* Render answer options as a user bubble for choice questions, only if showOptions is true */}
            {!showConfirmation && !isGreeting && currentQ && currentQ.type === 'choice' && showOptions && (
                <div className="message-container user-message">
                  <div className="message-content">
                    <div className="message-bubble user-bubble option-bubble">
                      <div className="option-buttons-inside">
                        {currentQ.options.map(option => (
                            <button key={option.label} className="option-button" onClick={() => handleOption(option.label)}>
                              <span role="img" aria-label="emoji">{option.emoji}</span> {String(option.label)}
                            </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
            )}
            {showOptions && currentQ.type === "multi-choice" && (
                <div className="message-container user-message">
                  <div className="message-content">
                    <div className="message-bubble user-bubble option-bubble">
                      <div className="interests-grid">
                        {INTERESTS.map(interest => (
                            <button
                                key={interest.label}
                                className={`interest-btn${multiSelect.includes(interest.label) ? ' selected' : ''}`}
                                onClick={() => handleMultiSelect(interest.label)}
                                type="button"
                            >
                              <span className="emoji">{interest.emoji}</span> {interest.label}
                            </button>
                        ))}
                      </div>
                      <div className="edit-controls" style={{ marginTop: 10 }}>
                        <button
                            className="send-button"
                            onClick={handleMultiSend}
                            disabled={multiSelect.length === 0}
                        >
                          <FiCheck />
                        </button>
                        <button className="skip-button" onClick={handleSkip}><FiX /></button>
                      </div>
                    </div>
                  </div>
                </div>
            )}
          </div>
          {showConfirmation ? (
              <div className="chatbot-footer">
                <button
                    className="continue-home-btn"
                    style={{ marginLeft: 0, marginRight: 'auto', background: '#ff914d', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 22px', fontWeight: 600, fontSize: '1em', cursor: 'pointer', boxShadow: '0 2px 8px rgba(255,145,77,0.07)' }}
                    onClick={() => {
                      if (startedFromProfile) {
                        onClose();
                      } else {
                        navigate('/home');
                      }
                    }}
                >
                  {startedFromProfile ? 'Save my changes' : 'Continue to the Home Screen'}
                </button>
              </div>
          ) : (
              !showConfirmation && !isGreeting && currentQ && (
                  <div className="chatbot-footer">
                    {/* שאלה פתוחה */}
                    {showOptions && currentQ.type === "open" && (
                        <form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                          <input
                              type="text"
                              className="chatbot-input"
                              value={input}
                              onChange={handleInput}
                              placeholder="Type your answer..."
                          />
                          <button type="submit" className="send-button">Send</button>
                        </form>
                    )}

                    {showOptions && shouldShowSkipButton() && (
                        <button className="skip-button" onClick={handleSkip}>
                          Skip
                        </button>
                    )}
                  </div>
              )
          )}
        </div>
      </div>
  );
}
export default OnboardingChatbot;